import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@antree/shared';
import { api } from '@/lib/api';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User | null) => void;
  fetchUser: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (
    email: string,
    password: string,
    name: string
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  verifyEmail: (token: string) => Promise<{ success: boolean; error?: string }>;
  resendVerification: () => Promise<{ success: boolean; error?: string }>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,

      setUser: (user) => {
        set({ user, isAuthenticated: !!user, isLoading: false });
      },

      fetchUser: async () => {
        set({ isLoading: true });
        const response = await api.get<{ user: User }>('/auth/me');
        if (response.success && response.data) {
          set({ user: response.data.user, isAuthenticated: true, isLoading: false });
        } else {
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      login: async (email, password) => {
        const response = await api.post<{ user: User }>('/auth/login', { email, password });
        if (response.success && response.data) {
          set({ user: response.data.user, isAuthenticated: true });
          return { success: true };
        }
        return { success: false, error: response.error?.code };
      },

      register: async (email, password, name) => {
        const response = await api.post<{ user: User }>('/auth/register', {
          email,
          password,
          name,
        });
        if (response.success && response.data) {
          set({ user: response.data.user, isAuthenticated: true });
          return { success: true };
        }
        return { success: false, error: response.error?.code };
      },

      logout: async () => {
        await api.post('/auth/logout');
        set({ user: null, isAuthenticated: false });
      },

      verifyEmail: async (token) => {
        const response = await api.post<{ message: string }>('/auth/verify-email', { token });
        if (response.success) {
          // Refresh user data
          await get().fetchUser();
          return { success: true };
        }
        return { success: false, error: response.error?.code };
      },

      resendVerification: async () => {
        const response = await api.post<{ message: string }>('/auth/resend-verification');
        return { success: response.success, error: response.error?.code };
      },
    }),
    {
      name: 'antree-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
